import { useState, useCallback, useRef } from "react";
import { Search as SearchIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { MapBounds } from "./ExploreMap";
import { track } from "@/lib/analytics";

interface SearchInAreaButtonProps {
  /** Current map bounds */
  bounds: MapBounds | null;
  /** Called when user clicks — parent should filter results by bounds */
  onSearchInArea: (bounds: MapBounds) => void;
  /** Whether button is visible */
  visible: boolean;
}

/**
 * "Sök i detta område" — the 12/10 signature for Explore map.
 * Shows only after a meaningful pan/zoom (debounced, not on initial load).
 * CTA never blinks — fade-in transition only.
 *
 * See docs/design-rules/ux-rules/explore-12-10-sot.md §6
 */
export function SearchInAreaButton({ bounds, onSearchInArea, visible }: SearchInAreaButtonProps) {
  if (!visible || !bounds) return null;

  return (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 animate-in fade-in duration-300">
      <Button
        variant="default"
        size="sm"
        className="rounded-full shadow-lg gap-2 px-4 h-9 text-xs font-medium"
        onClick={() => {
          track("explore_search_in_area_clicked", {}, "explore");
          onSearchInArea(bounds);
        }}
      >
        <SearchIcon className="h-3.5 w-3.5" />
        Sök i detta område
      </Button>
    </div>
  );
}

/** Threshold: center must shift by >15% of viewport span */
const SHIFT_THRESHOLD = 0.15;
/** Debounce ms before showing button */
const DEBOUNCE_MS = 600;

function boundsCenter(b: MapBounds) {
  return {
    lat: (b.north + b.south) / 2,
    lng: (b.east + b.west) / 2,
  };
}

function boundsSpan(b: MapBounds) {
  return {
    lat: Math.abs(b.north - b.south),
    lng: Math.abs(b.east - b.west),
  };
}

/**
 * Hook to manage "Search in area" state.
 * Shows button only after a meaningful pan/zoom from the reference bounds.
 * Uses debounce to prevent blink/flash during continuous pan.
 * Requires explicit user interaction (not triggered on initial load or auto-fit).
 */
export function useSearchInArea() {
  const [showButton, setShowButton] = useState(false);
  const [activeBounds, setActiveBounds] = useState<MapBounds | null>(null);
  const referenceBoundsRef = useRef<MapBounds | null>(null);
  const hasUserInteractedRef = useRef(false);
  const moveCountRef = useRef(0);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleBoundsChange = useCallback((bounds: MapBounds) => {
    moveCountRef.current += 1;

    // First 2 moves are typically initial load + auto-fit — skip
    if (moveCountRef.current <= 2) {
      referenceBoundsRef.current = bounds;
      return;
    }

    // After 2 moves, user has interacted
    hasUserInteractedRef.current = true;

    if (!referenceBoundsRef.current) {
      referenceBoundsRef.current = bounds;
      return;
    }

    // Check meaningful shift
    const refCenter = boundsCenter(referenceBoundsRef.current);
    const newCenter = boundsCenter(bounds);
    const span = boundsSpan(bounds);

    const latShift = Math.abs(newCenter.lat - refCenter.lat);
    const lngShift = Math.abs(newCenter.lng - refCenter.lng);

    const meaningful =
      latShift > span.lat * SHIFT_THRESHOLD ||
      lngShift > span.lng * SHIFT_THRESHOLD;

    if (!meaningful) return;

    // Debounce: don't flash button during continuous drag
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setActiveBounds(bounds);
      setShowButton(true);
      debounceTimerRef.current = null;
    }, DEBOUNCE_MS);
  }, []);

  const handleSearchInArea = useCallback((bounds: MapBounds) => {
    setShowButton(false);
    referenceBoundsRef.current = bounds;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    return bounds;
  }, []);

  const reset = useCallback(() => {
    setShowButton(false);
    setActiveBounds(null);
    referenceBoundsRef.current = null;
    hasUserInteractedRef.current = false;
    moveCountRef.current = 0;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  return {
    showButton,
    activeBounds,
    handleBoundsChange,
    handleSearchInArea,
    reset,
  };
}
